import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REVIEW_COLLECTIONS = ['cleaning_history', 'cleaning_jobs', 'cleaning_clients', 'cleaner_invoices', 'cleaner_payments'];
const LABEL = { cleaning_history: 'Building Cleaning', cleaning_jobs: 'Cleaning Jobs', cleaning_clients: 'Πελάτες καθαρισμού', cleaner_invoices: 'Τιμολόγια καθαριστριών', cleaner_payments: 'Πληρωμές καθαριστριών' };
const HEAD = { cleaning_history: ['Ημ/νία', 'Κτίριο', 'Καθαρίστρια', 'Ποσό'], cleaner_invoices: ['Ημ/νία', 'Αριθμός', 'Καθαρίστρια', 'Σύνολο'], cleaner_payments: ['Ημ/νία', 'Πληρωμή', 'Καθαρίστρια', 'Ποσό'], default: ['Ημ/νία', 'Πελάτης', 'Καθαρίστριες', 'Ποσό'] };

const Numbering = ({ n }) => (
  <div className="text-xs border border-sky-200 bg-sky-50 p-2 mb-2" data-testid="review-numbering">
    <b>Αρίθμηση:</b> Live τελευταίος αριθμός <span className="font-mono">{n.live_last_number || '—'}</span> · μετρητής Live <span className="font-mono">{n.live_counter ?? '—'}</span> · νέοι αριθμοί <span className="font-mono">{n.import_numbers.join(', ') || '—'}</span>
    {n.collisions.length ? <span className="text-red-700 font-bold"> · ΣΥΓΚΡΟΥΣΗ: {n.collisions.join(', ')}</span> : <span className="text-emerald-700"> · χωρίς διπλούς αριθμούς</span>}
    {n.counter_raise && <div className="text-amber-800 mt-1">Ο μετρητής πρέπει να ανέβει {n.counter_raise.live} → {n.counter_raise.preview} (τικ «αύξηση μετρητών» στην έγκριση), αλλιώς το επόμενο τιμολόγιο του Live θα πάρει ξανά τον αριθμό {n.import_numbers[0]}.</div>}
  </div>
);

const RecordsTable = ({ data, excluded, toggle, setAll }) => {
  const head = HEAD[data.collection] || HEAD.default;
  const included = data.rows.filter((r) => !excluded.has(r._id));
  const sum = included.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  return (
    <div data-testid={`review-table-${data.collection}`}>
      {data.numbering && <Numbering n={data.numbering} />}
      <div className="flex flex-wrap items-center gap-3 text-xs mb-1">
        <span>Θα μεταφερθούν <b data-testid={`review-included-${data.collection}`}>{included.length}</b> / {data.rows.length} · σύνολο ποσών <b>{sum.toFixed(2)} €</b></span>
        <button className="underline" onClick={() => setAll(data.rows.filter((r) => r.test_reason).map((r) => r._id))} data-testid={`review-exclude-tests-${data.collection}`}>Εξαίρεση μόνο δοκιμαστικών</button>
        <button className="underline" onClick={() => setAll([])} data-testid={`review-include-all-${data.collection}`}>Επιλογή όλων</button>
      </div>
      <div className="border border-zinc-200 bg-white max-h-[420px] overflow-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-zinc-900 text-white text-[10px] uppercase"><tr><th className="px-2 py-1 w-6" />{[...head, 'Σημειώσεις', 'Δημιουργός', 'Κατάσταση'].map((h) => <th key={h} className="text-left px-2 py-1">{h}</th>)}</tr></thead>
          <tbody>{data.rows.map((r) => (
            <tr key={r._id} className={`border-t border-zinc-100 ${r.test_reason ? 'bg-red-50' : ''} ${excluded.has(r._id) ? 'opacity-50' : ''}`} data-testid={`review-row-${r._id}`}>
              <td className="px-2 py-1"><input type="checkbox" checked={!excluded.has(r._id)} onChange={() => toggle(r._id)} data-testid={`review-check-${r._id}`} /></td>
              <td className="px-2 py-1 font-mono whitespace-nowrap">{r.date}</td><td className="px-2 py-1">{r.client}</td><td className="px-2 py-1">{r.cleaners}</td>
              <td className="px-2 py-1 font-mono text-right">{r.amount ?? '—'}</td><td className="px-2 py-1 text-zinc-500 max-w-[220px] truncate" title={r.notes}>{r.notes}</td>
              <td className="px-2 py-1 text-zinc-500">{r.creator}</td>
              <td className="px-2 py-1">{r.test_reason && <span className="text-red-700">{r.test_reason}</span>}{r.is_deleted && <span className="text-zinc-500"> · διαγραμμένη</span>}{r.broken && <span className="text-red-700"> · σπασμένη συσχέτιση</span>}</td>
            </tr>))}</tbody>
        </table>
      </div>
    </div>
  );
};

export const RecordReview = ({ report, excludeIds, setExcludeIds }) => {
  const [data, setData] = useState({});
  const [active, setActive] = useState('');
  const [busy, setBusy] = useState(false);
  const open = async (coll) => {
    setActive(coll);
    if (data[coll]) return;
    setBusy(true);
    try {
      const r = (await axios.get(`${API}/admin/data-sync/reports/${report.report_id}/records`, { params: { collection: coll }, timeout: 300000 })).data;
      setData((d) => ({ ...d, [coll]: r }));
      if (!excludeIds[coll]) setExcludeIds({ ...excludeIds, [coll]: new Set(r.rows.filter((x) => x.test_reason).map((x) => x._id)) });
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setBusy(false); }
  };
  const ex = excludeIds[active] || new Set();
  const toggle = (id) => { const n = new Set(ex); if (n.has(id)) n.delete(id); else n.add(id); setExcludeIds({ ...excludeIds, [active]: n }); };
  const present = REVIEW_COLLECTIONS.filter((c) => report.collections.some((x) => x.collection === c && x.importable));
  return (
    <div className="border border-zinc-300 bg-white p-3 mt-4" data-testid="data-sync-record-review">
      <h3 className="text-sm font-bold mb-1">Έλεγχος ακριβών εγγραφών πριν το Import</h3>
      <p className="text-xs text-zinc-500 mb-2">Οι δοκιμαστικές εγγραφές (κόκκινες) είναι ήδη ξετσεκαρισμένες. Ό,τι ξετσεκάρετε δεν θα μεταφερθεί.</p>
      <div className="flex flex-wrap gap-1 mb-2">{present.map((c) => (
        <Button key={c} size="sm" variant={active === c ? 'default' : 'outline'} className="rounded-none h-7" disabled={busy} onClick={() => open(c)} data-testid={`review-open-${c}`}>
          {LABEL[c]}{excludeIds[c] ? ` (−${excludeIds[c].size})` : ''}
        </Button>))}</div>
      {busy && <div className="text-xs text-zinc-400">Φόρτωση…</div>}
      {active && data[active] && <RecordsTable data={data[active]} excluded={ex} toggle={toggle} setAll={(ids) => setExcludeIds({ ...excludeIds, [active]: new Set(ids) })} />}
    </div>
  );
};

export const DeletedInPreview = ({ report }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try { setData((await axios.get(`${API}/admin/data-sync/reports/${report.report_id}/deleted-in-preview`, { timeout: 300000 })).data); } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setBusy(false); }
  };
  return (
    <div className="border border-amber-300 bg-amber-50/40 p-3 mt-4" data-testid="data-sync-deleted-in-preview">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-bold">Διαγραμμένες στο Preview αλλά ενεργές στο Live</h3>
        <Button size="sm" variant="outline" className="rounded-none h-7" disabled={busy} onClick={load} data-testid="deleted-in-preview-load-btn">{busy ? 'Φόρτωση…' : 'Εμφάνιση λίστας'}</Button>
      </div>
      <p className="text-xs text-zinc-600 mt-1">Μόνο για έλεγχο — δεν γίνεται καμία αυτόματη διαγραφή ή τροποποίηση. Όποιες επιβεβαιώσετε, διαγράφονται χειροκίνητα στο Live.</p>
      {data && (
        <>
          <div className="text-xs my-1" data-testid="deleted-in-preview-total">{data.rows.length} εγγραφές · σύνολο αμοιβών καθαριστριών <b>{data.total_amount.toFixed(2)} €</b></div>
          <div className="border border-zinc-200 bg-white max-h-[420px] overflow-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-zinc-900 text-white text-[10px] uppercase"><tr>{['#', 'Ενότητα', 'Ημ/νία', 'Πελάτης / Κτίριο', 'Καθαρίστριες (αμοιβή)', 'Σύνολο', 'Σημειώσεις', 'Διαγράφηκε στο Preview'].map((h) => <th key={h} className="text-left px-2 py-1">{h}</th>)}</tr></thead>
              <tbody>{data.rows.map((r, i) => (
                <tr key={r._id} className="border-t border-zinc-100" data-testid={`deleted-row-${r._id}`}>
                  <td className="px-2 py-1">{i + 1}</td><td className="px-2 py-1 font-mono">{r.collection}</td><td className="px-2 py-1 font-mono whitespace-nowrap">{r.date}</td>
                  <td className="px-2 py-1">{r.client}</td><td className="px-2 py-1">{r.cleaners}</td><td className="px-2 py-1 font-mono text-right">{r.amount}</td>
                  <td className="px-2 py-1 text-zinc-500">{r.notes}</td><td className="px-2 py-1 font-mono text-zinc-500">{r.deleted_in_preview_at.replace('T', ' ')}</td>
                </tr>))}</tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};
